'use client';

import React, { useMemo, useState } from 'react';
import Image from 'next/image';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { BASE_FILE_URL } from '@/common/constants';

// Map nhãn badge -> màu gradient (theo file HTML gốc)
const BADGE_STYLES = {
  'Đặc sản': 'from-[#f9a825] to-[#f57f17]',
  'OCOP 3 sao': 'from-[#e65100] to-[#ff6d00]',
  'OCOP 4 sao': 'from-[#e65100] to-[#ff6d00]',
  'Hữu cơ': 'from-[#2e7d32] to-[#43a047]',
};

const DEFAULT_BADGE_STYLE = 'from-[#f9a825] to-[#f57f17]';

const CATEGORY_ICONS = {
  'trai-cay': (
    <>
      <path d="M12 2a5 5 0 0 0-5 5c0 2.5 2 4.9 5 8 3-3.1 5-5.5 5-8a5 5 0 0 0-5-5Z" />
      <path d="M12 12v9" />
    </>
  ),
  'chan-nuoi': (
    <>
      <path d="M12 3c-4 0-6 3-6 6 0 4 3 5 3 8h6c0-3 3-4 3-8 0-3-2-6-6-6Z" />
    </>
  ),
  'che-bien': (
    <>
      <path d="M4 12h16v2a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6v-2Z" />
      <path d="M8 12V7a4 4 0 0 1 8 0v5" />
    </>
  ),
  'rau-rung': (
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </>
  ),
};

const DEFAULT_ICON = (
  <>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </>
);

const IconWrap = ({ children, size = 14, stroke = 'currentColor' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    {children}
  </svg>
);

const StarIcon = (props) => (
  <IconWrap {...props}>
    <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 21 12 17.77 6.2 21l1.3-7.35L3 9.27l6.1-1.01L12 2z" />
  </IconWrap>
);

const LocationIcon = (props) => (
  <IconWrap {...props} stroke="#b71c1c">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </IconWrap>
);

const LocalPageProduct = ({ localProducts, titlePage }) => {
  const items = get(localProducts, 'results', []);
  const [activeTab, setActiveTab] = useState('all');

  // TODO: đổi 'category.slug' / 'category.name' nếu API trả về field khác (vd. 'menu.slug')
  const groups = useMemo(() => {
    const grouped = groupBy(items, (item) => get(item, 'type', 'khac'));
    return Object.keys(grouped).map((slug) => ({
      slug,
      name: get(grouped[slug], '[0].type', 'Khác'),
      items: grouped[slug],
    }));
  }, [items]);

  if (!items.length) {
    return (
      <section className="py-12 bg-white">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
            <h3 className="text-lg font-semibold text-[#b71c1c] mb-2">{titlePage || 'Sản phẩm địa phương'}</h3>
            <p>Chưa có sản phẩm nào để hiển thị.</p>
          </div>
        </div>
      </section>
    );
  }

  const visibleGroups = activeTab === 'all' ? groups : groups.filter((g) => g.slug === activeTab);

  return (
    <section className="py-12 bg-white">
      <div className="max-w-screen-xl mx-auto px-4">
        {/* Hero / Header */}
        <div className="mb-6 pb-4 border-b-2 border-[#b71c1c] flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="text-[#b71c1c] text-xl font-bold uppercase flex items-center gap-2">
              <StarIcon size={20} stroke="currentColor" />
              Sản Vật Địa Phương
            </h2>
            <p className="text-gray-500 text-sm mt-1">Tinh hoa nông sản vùng cao Bằng Lang – Tuyên Quang</p>
          </div>

          {/* Filter tabs */}
          {groups.length > 1 && (
            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  activeTab === 'all'
                    ? 'bg-[#b71c1c] text-white border-[#b71c1c]'
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:border-[#b71c1c] hover:text-[#b71c1c]'
                }`}>
                Tất cả
              </button>
              {groups.map((g) => (
                <button
                  key={g.slug}
                  type="button"
                  onClick={() => setActiveTab(g.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    activeTab === g.slug
                      ? 'bg-[#b71c1c] text-white border-[#b71c1c]'
                      : 'bg-gray-100 text-gray-700 border-gray-200 hover:border-[#b71c1c] hover:text-[#b71c1c]'
                  }`}>
                  {g.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category sections */}
        {visibleGroups.map((group) => (
          <div key={group.slug} className="mb-10 last:mb-0">
            <div className="flex items-center justify-between pb-3 mb-5 border-b-2 border-[#b71c1c]">
              <h3 className="text-lg font-extrabold text-[#b71c1c] flex items-center gap-2">
                <IconWrap size={18}>{CATEGORY_ICONS[group.slug] || DEFAULT_ICON}</IconWrap>
                {group.name}
              </h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-3.5 py-1 rounded-full">{group.items.length} sản phẩm</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {group.items.map((item) => (
                <div
                  key={item.id || item.slug}
                  className="group bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-1 hover:border-[#b71c1c] transition-all duration-300">
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <Image
                      src={`${BASE_FILE_URL}/${item.avatarUrl}`}
                      alt={item.title || item.name || ''}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-400"
                      quality={85}
                    />
                    {item.type && (
                      <span
                        className={`absolute top-3 right-3 bg-gradient-to-br ${
                          BADGE_STYLES[item.type] || DEFAULT_BADGE_STYLE
                        } text-white text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded flex items-center gap-1`}>
                        <StarIcon size={10} stroke="#fff" />
                        {item.type}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h4 className="text-[15px] font-bold text-gray-800 group-hover:text-[#b71c1c] transition-colors duration-200 mb-1.5 line-clamp-1">
                      {item.title || item.name}
                    </h4>
                    {item.desc && <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 mb-2.5">{item.desc}</p>}
                    {item.address && (
                      <span className="text-[12px] text-gray-400 flex items-center gap-1.5">
                        <LocationIcon size={11} />
                        {item.address}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default LocalPageProduct;
